/**
 * Thread Assignment Service
 *
 * Service for managing thread assignments to users.
 * Backend: POST /chat/threads/assign (chat.py)
 */

import { isAxiosError } from 'axios';
import api from './api/api';
import { API_ENDPOINTS } from './api/config';

export interface ThreadDetails {
  id: string;
  user_id: string;
  currentUserId?: string;
  name?: string;
  title?: string;
  assigned_at?: string;
  tags?: string[];
  context?: Record<string, unknown>;
}

export interface ThreadAssignmentRequest {
  threadId: string;
  userId: string;
}

export interface ThreadAssignmentResponse {
  success: boolean;
  message: string;
  thread?: {
    id: string;
    name: string;
    user_id: string;
    assigned_at: string;
  };
}

export class ThreadAssignmentService {
  /**
   * Assign a thread to a user
   */
  static async assignThreadToUser(
    threadId: string, 
    userId: string
  ): Promise<ThreadAssignmentResponse> {
    try {
      const response = await api.post(API_ENDPOINTS.THREADS.ASSIGN, {
        thread_id: threadId,
        user_id: userId
      });
      
      return {
        success: true,
        message: 'Thread assigned successfully',
        thread: response.data
      };
    } catch (error: unknown) {
      console.error('Failed to assign thread:', error);
      const message =
        isAxiosError(error) && error.response?.data?.detail
          ? String(error.response.data.detail)
          : 'Failed to assign thread';
      return { success: false, message };
    }
  }

  /**
   * Get thread details for assignment dialog
   */
  static async getThreadDetails(threadId: string): Promise<ThreadDetails> {
    try {
      const response = await api.get<ThreadDetails>(API_ENDPOINTS.THREADS.BY_ID(threadId));
      return response.data;
    } catch (error: unknown) {
      console.error('Failed to get thread details:', error);
      throw new Error('Failed to get thread details');
    }
  }

  /**
   * Check if thread is assigned to user
   */
  static async isThreadAssignedToUser(
    threadId: string, 
    userId: string
  ): Promise<boolean> {
    try {
      const thread = await this.getThreadDetails(threadId);
      return thread.user_id === userId;
    } catch (error) {
      console.error('Failed to check thread assignment:', error);
      return false;
    }
  }
}
